import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Copy, Download, RefreshCw, Trash2 } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ROUTES,
  DYNAMIC_APPTYPE_OPTIONS,
  getUserTypesByAppType,
  USER_TYPE_LABELS,
} from '@/utils/constants';
import { useGetModuleDocAis, useDeleteModuleDocAi } from './hooks/useModuleDocAi';
import { ModuleDocAiRecord } from './types';
import { ActionCell } from '@/components/common/ActionCell';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const ModuleDocAiPage = () => {
  const navigate = useNavigate();

  // Filter States
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUserType, setSelectedUserType] = useState<string>('ALL');
  const [selectedAppType, setSelectedAppType] = useState<string>('ALL');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Debounce search term to prevent rapid API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Construct query parameters
  const params = useMemo(() => {
    return {
      search: debouncedSearch ? debouncedSearch : undefined,
      userType: selectedUserType && selectedUserType !== 'ALL' ? selectedUserType : undefined,
      appType: selectedAppType && selectedAppType !== 'ALL' ? selectedAppType : undefined,
    };
  }, [debouncedSearch, selectedUserType, selectedAppType]);

  const availableFilterUserTypes = useMemo(() => {
    if (selectedAppType !== 'ALL') {
      return getUserTypesByAppType(selectedAppType);
    }
    return Object.entries(USER_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
    }));
  }, [selectedAppType]);

  const { data, isLoading } = useGetModuleDocAis(params);
  const { mutate: deleteEntry } = useDeleteModuleDocAi();

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const handleReset = () => {
    setSearch('');
    setDebouncedSearch('');
    setSelectedUserType('ALL');
    setSelectedAppType('ALL');
    toast.success('Filters reset successfully');
  };

  const columns = useMemo<ColumnDef<ModuleDocAiRecord>[]>(
    () => [
      {
        accessorKey: 'moduleName',
        header: 'Module Name',
        cell: ({ row }) => (
          <span className="font-semibold text-primary">{row.original.moduleName}</span>
        ),
      },
      {
        accessorKey: 'moduleCode',
        header: 'Module Code',
        cell: ({ row }) => (
          <code className="text-xs font-mono bg-muted/60 text-foreground px-2 py-0.5 rounded border border-border/40">
            {row.original.moduleCode}
          </code>
        ),
      },
      {
        accessorKey: 'userType',
        header: 'User Type',
        cell: ({ row }) => {
          const userType = row.original.userType;
          if (!userType) return <span className="text-muted-foreground">—</span>;
          return <span className="text-sm font-medium">{userType.replace(/_/g, ' ')}</span>;
        },
      },
      {
        accessorKey: 'appType',
        header: 'App Type',
        cell: ({ row }) => {
          const appType = row.original.appType;
          if (!appType) return <span className="text-muted-foreground">—</span>;
          return (
            <Badge
              variant="outline"
              className="font-bold text-[10px] tracking-wider uppercase border-border/80 bg-muted/40 text-foreground"
            >
              {appType.replace(/_/g, ' ')}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'fileStorageId',
        header: 'File Storage ID',
        cell: ({ row }) => (
          <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground font-mono truncate max-w-[120px] inline-block">
            {row.original.fileStorageId}
          </code>
        ),
      },
      {
        id: 'documentActions',
        header: 'File Link',
        cell: ({ row }) => {
          const filePath = row.original.filePath;

          const handleCopy = async () => {
            if (!filePath) return;
            try {
              await navigator.clipboard.writeText(filePath);
              toast.success('File link copied to clipboard!');
            } catch {
              toast.error('Failed to copy file link');
            }
          };

          const handleDownload = () => {
            if (!filePath) return;
            window.open(filePath, '_blank', 'noopener,noreferrer');
          };

          if (!filePath) {
            return <span className="text-[11px] text-muted-foreground italic">No File Url</span>;
          }

          return (
            <div className="flex items-center gap-1.5">
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopy}
                      className="h-8 w-8 hover:border-primary/50 hover:bg-primary/5 text-muted-foreground hover:text-foreground"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy link</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleDownload}
                      className="h-8 w-8 hover:border-primary/50 hover:bg-primary/5 text-muted-foreground hover:text-foreground"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download file</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => {
          const recordId = row.original.id || row.original._id || '';
          return (
            <div className="flex justify-end">
              <ActionCell
                onEdit={() => navigate(ROUTES.EDIT_MODULE_DOC_AI.replace(':id', recordId))}
                onDelete={() => handleDelete(recordId)}
              />
            </div>
          );
        },
      },
    ],
    [navigate],
  );

  const hasActiveFilters = search || selectedUserType !== 'ALL' || selectedAppType !== 'ALL';

  return (
    <div className="w-full  py-6 space-y-6 animate-fade-in">
      <PageHeader
        title="Module Doc AI"
        subtitle="Manage dynamic application modules, permissions, and file mapping."
      >
        <Button
          onClick={() => navigate(ROUTES.ADD_MODULE_DOC_AI)}
          className="bg-primary hover:bg-primary/90 text-white w-full sm:w-auto justify-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Doc
        </Button>
      </PageHeader>

      {/* Responsive Filter Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 bg-muted/20 p-4 rounded-xl border border-border/60">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search module name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 w-full"
          />
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <div className="w-full sm:w-[180px]">
            <Select
              value={selectedAppType}
              onValueChange={(val) => {
                setSelectedAppType(val);
                if (val !== 'ALL' && selectedUserType !== 'ALL') {
                  const validUsers = getUserTypesByAppType(val);
                  if (!validUsers.some((u) => u.value === selectedUserType)) {
                    setSelectedUserType('ALL');
                  }
                }
              }}
            >
              <SelectTrigger className="h-11 w-full">
                <SelectValue placeholder="App Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All App Types</SelectItem>
                {DYNAMIC_APPTYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[180px]">
            <Select value={selectedUserType} onValueChange={setSelectedUserType}>
              <SelectTrigger className="h-11 w-full">
                <SelectValue placeholder="User Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All User Types</SelectItem>
                {availableFilterUserTypes.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={handleReset}
              className="h-11 w-full sm:w-auto px-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground justify-center animate-fade-in"
            >
              <RefreshCw size={14} className="mr-2" />
              Reset
            </Button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.data || []}
        isLoading={isLoading}
        useInternalPagination
        emptyMessage="No Module Doc AI configurations found"
        emptyDescription="Create a new module configuration entry to get started."
        showRowNumbers
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-xl p-8 max-w-[400px] border border-border bg-card px-10">
          <AlertDialogHeader className="mb-6">
            <div className="w-12 h-12 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={24} />
            </div>
            <AlertDialogTitle className="text-xl font-bold text-center">
              Delete Record?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-muted-foreground mt-2">
              Are you sure you want to delete this module doc record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-3">
            <AlertDialogCancel className="w-full sm:w-32 rounded-lg h-10 border-border font-medium hover:bg-accent transition-colors">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteId) {
                  deleteEntry(deleteId);
                  setDeleteId(null);
                }
              }}
              className="w-full sm:w-32 rounded-lg h-10 bg-red-600 hover:bg-red-700 text-white border-none font-medium transition-all"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModuleDocAiPage;
